import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Row, Col, Tag, Table } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { PieChart } from 'echarts/charts';
import { TooltipComponent, LegendComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import * as dashboardService from '../../../services/dashboard/dashboardService';
import { ShortageReasonOptions, CancelReasonOptions } from '../../../constants/picking';

echarts.use([PieChart, TooltipComponent, LegendComponent, CanvasRenderer]);

const COLORS = {
  success: '#52C41A',
  warning: '#FAAD14',
  danger: '#FF4D4F',
};

const PIE_COLORS = ['#1890ff', '#ff7a45', '#faad14', '#722ed1', '#13c2c2'];

const CANCEL_COLORS = ['#ff4d4f', '#ff7875', '#ffa39e', '#ffccc7', '#ffc4b0', '#ffd8d2'];

/**
 * Module 4: Quality Control & Stock Analysis
 * Rate Cards + Pie (shortage/cancel reasons) + Top 10 table
 * Supports cross-filtering: clicking a top shortage item row filters the shortage reason pie
 */
const QualityPanel = ({ data, channel }) => {
  const { t } = useTranslation();
  // selectedItem 保存商品编码（itemCode/sku），与 bar yAxis 一致
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemReasons, setItemReasons] = useState([]);
  const [itemReasonsLoading, setItemReasonsLoading] = useState(false);

  // 点击 Top10 商品时按需请求该 SKU 维度的缺货原因分布
  useEffect(() => {
    if (!selectedItem) {
      setItemReasons([]);
      return;
    }
    let cancelled = false;
    setItemReasonsLoading(true);
    dashboardService
      .getItemShortageReasons(selectedItem, channel)
      .then((resp) => {
        if (cancelled) return;
        if (resp?.success) {
          setItemReasons(resp.data || []);
        } else {
          setItemReasons([]);
        }
      })
      .catch(() => {
        if (!cancelled) setItemReasons([]);
      })
      .finally(() => {
        if (!cancelled) setItemReasonsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedItem, channel]);

  const handleItemClick = useCallback((itemCode) => {
    setSelectedItem((prev) => (prev === itemCode ? null : itemCode));
  }, []);

  // Helper: get color for rate value
  const getRateColor = (value, thresholdYellow = 3, thresholdRed = 5) => {
    return value >= thresholdRed ? COLORS.danger : value >= thresholdYellow ? COLORS.warning : COLORS.success;
  };

  // 后端返回的缺货/取消原因为枚举 code（如 STOCK_OH_ZERO_OR_NEGATIVE），需映射为可读文案
  const mapReason = (code) => {
    const opt = ShortageReasonOptions.find((o) => o.value === code);
    return opt ? t(opt.label) : code;
  };
  const mapCancelReason = (code) => {
    const opt = CancelReasonOptions.find((o) => o.value === code);
    return opt ? t(opt.label) : code;
  };

  if (!data) return null;

  // 当选中某个商品时使用接口返回的该 SKU 维度原因分布；否则显示全局分布
  const shortageReasonRaw = selectedItem ? itemReasons : (data.shortageReasons || []);
  // 将枚举 code 映射为可读文案后再用于饼图与图例
  const shortageReasonData = shortageReasonRaw.map((d) => ({ ...d, reason: mapReason(d.reason) }));
  // 取消原因同样需要映射
  const cancelReasonData = (data.cancelReasons || []).map((d) => ({ ...d, reason: mapCancelReason(d.reason) }));

  // 选中商品的友好名称（优先 itemName，其次 itemCode）
  const selectedItemLabel = selectedItem
    ? (data.topShortageItems?.find((i) => i.sku === selectedItem)?.itemName || selectedItem)
    : null;

  // Shortage reason pie
  const shortageReasonOption = {
    tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)', position: (point) => [point[0] + 12, point[1]] },
    series: [{
      type: 'pie',
      radius: ['40%', '66%'],
      center: ['38%', '50%'],
      label: { show: false },
      labelLine: { show: false },
      emphasis: {
        itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.3)' },
      },
      data: shortageReasonData.map((d, i) => ({
        value: d.count,
        name: d.reason,
        itemStyle: { color: PIE_COLORS[i % PIE_COLORS.length] },
      })),
    }],
  };

  // Cancel reason pie
  const cancelReasonOption = {
    tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)', position: (point) => [point[0] + 12, point[1]] },
    series: [{
      type: 'pie',
      radius: ['40%', '68%'],
      center: ['38%', '50%'],
      label: { show: false },
      labelLine: { show: false },
      emphasis: {
        itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.3)' },
      },
      data: cancelReasonData.map((d, i) => ({
        value: d.count,
        name: d.reason,
        itemStyle: { color: CANCEL_COLORS[i % CANCEL_COLORS.length] },
      })),
    }],
  };

  // Top shortage items table (click a row to cross-filter the reason pie)
  const shortageItems = data.topShortageItems || [];
  const displayItems = shortageItems.slice(0, 10);

  const shortageColumns = [
    {
      title: t('dashboard.quality.skuCol'),
      dataIndex: 'sku',
      key: 'sku',
      width: 120,
      ellipsis: true,
      // 链接样式 + 下划线，提示该单元格可点击下钻缺货原因
      render: (value) => (
        <a style={{ color: '#1677ff', textDecoration: 'underline' }}>{value}</a>
      ),
    },
    {
      title: t('dashboard.quality.nameCol'),
      dataIndex: 'itemName',
      key: 'itemName',
      ellipsis: true,
      render: (value) => value || '-',
    },
    {
      title: t('dashboard.quality.count'),
      dataIndex: 'count',
      key: 'count',
      width: 70,
      align: 'right',
    },
  ];

  // Rate cards configuration
  const rateCards = [
    { label: t('dashboard.quality.orderOOSRate'), value: `${data.orderShortageRate}%`, color: getRateColor(data.orderShortageRate) },
    { label: t('dashboard.quality.itemOOSRate'), value: `${data.itemShortageRate}%`, color: getRateColor(data.itemShortageRate) },
    { label: t('dashboard.quality.substituteRate'), value: `${data.substitutionRate}%`, color: getRateColor(data.substitutionRate) },
    { label: t('dashboard.quality.avgShortageHandling'), value: `${data.shortageHandlingTime} ${t('dashboard.min')}`, color: COLORS.warning },
    { label: t('dashboard.quality.orderCancelRate'), value: `${data.orderCancelRate}%`, color: getRateColor(data.orderCancelRate), isAlert: true },
  ];

  const renderReasonLegend = (items, colors) => (
    <div style={{ flex: 1, minWidth: 0, marginLeft: -36, maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      {items.map((item, index) => (
        <div
          key={`${item.reason}-${index}`}
          title={item.reason}
          style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 6, minWidth: 0 }}
        >
          <span
            style={{
              width: 9,
              height: 9,
              borderRadius: 2,
              background: colors[index % colors.length],
              flex: '0 0 9px',
              marginTop: 3,
            }}
          />
          <span style={{ fontSize: 10, lineHeight: '15px', color: '#666', wordBreak: 'break-word' }}>
            {item.reason}
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <Card title={t('dashboard.quality.title')} style={{ height: '100%' }} styles={{ body: { padding: 16 } }}>
      {/* Top: 5 Rate Cards - 横向展示 */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        {rateCards.map((card, index) => (
          <div
            key={index}
            style={{
              flex: 1,
              background: card.isAlert ? '#fff2f0' : '#fafafa',
              border: card.isAlert ? '1px solid #ffccc7' : 'none',
              borderRadius: 8,
              padding: '12px 16px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              textAlign: 'center',
              minWidth: 0,
            }}
          >
            {card.isAlert && <WarningOutlined style={{ fontSize: 14, color: COLORS.danger, marginBottom: 4 }} />}
            <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>{card.label}</div>
            <div style={{ fontSize: 24, fontWeight: 'bold', color: card.color }}>
              {card.value}
            </div>
          </div>
        ))}
      </div>

      {/* Bottom: Charts Area */}
      <div style={{ display: 'flex', gap: 16 }}>
        {/* Left: OOS Reason Breakdown Pie */}
        <div style={{ flex: '0 0 30%', minWidth: 0 }}>
          <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13, textAlign: 'left' }}>
            {t('dashboard.quality.oosReasonBreakdown')}
            {selectedItem && (
              <Tag color="blue" closable onClose={() => setSelectedItem(null)} style={{ marginLeft: 8, marginTop: 4 }}>
                {selectedItemLabel}
              </Tag>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 380, minWidth: 0 }}>
            <ReactEChartsCore
              echarts={echarts}
              option={shortageReasonOption}
              style={{ height: 280, width: 200, flex: '0 0 200px' }}
              opts={{ renderer: 'canvas' }}
              showLoading={itemReasonsLoading}
              key={selectedItem || 'all'}
            />
            {renderReasonLegend(shortageReasonData, PIE_COLORS)}
          </div>
        </div>

        {/* Center: Cancel Reason Breakdown Pie */}
        <div style={{ flex: '0 0 30%', minWidth: 0 }}>
          <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13, textAlign: 'left' }}>{t('dashboard.quality.cancelReasonBreakdown')}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 380, minWidth: 0 }}>
            <ReactEChartsCore
              echarts={echarts}
              option={cancelReasonOption}
              style={{ height: 280, width: 200, flex: '0 0 200px' }}
              opts={{ renderer: 'canvas' }}
            />
            {renderReasonLegend(cancelReasonData, CANCEL_COLORS)}
          </div>
        </div>

        {/* Right: Top 10 shortage items */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13 }}>
            {t('dashboard.quality.topShortageItems')}
            {selectedItem && (
              <Tag color="green" closable onClose={() => setSelectedItem(null)} style={{ marginLeft: 8 }}>
                {t('dashboard.quality.selected')}: {selectedItemLabel}
              </Tag>
            )}
          </div>
          <Table
            size="small"
            rowKey="sku"
            columns={shortageColumns}
            dataSource={displayItems}
            pagination={false}
            scroll={{ y: 340 }}
            onRow={(record) => ({
              onClick: () => handleItemClick(record.sku),
              style: {
                cursor: 'pointer',
                background: selectedItem === record.sku ? '#e6f4ff' : undefined,
              },
            })}
          />
        </div>
      </div>
    </Card>
  );
};

export default QualityPanel;
